import { Add, Close, Insights } from '@mui/icons-material';
import { Button, ClickAwayListener, IconButton, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { CommodityManagementSiteAccessModel, CustomerReportModel, GetCustomerDataResponse, GetCustomerSettingsResponse, ReportType } from '../../../../Generated/Commodity-Management-Api';
import { useMtmDate } from '../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXRow, StoneXTab, StoneXTabs } from '../../../StoneX';
import StoneXMainPage from '../../../StoneX/StoneXMainPage/StoneXMainPage';
import { PositionReport } from './Reports/PositionReport/PositionReport';
import { ProfitLossReport } from './Reports/ProfitLoss/ProfitLossReport';
import ScenarioReport from './Reports/Scenario/ScenarioReport';
import SummaryReport from './Reports/Summary/SummaryReport';
import TimeTriggerReport from './Reports/TimeTrigger/TimeTriggerReport';
import VarianceReport from './Reports/Variance/VarianceReport';
import { ExposureReport } from './Reports/Exposure/ExposureReport';
import { DailyPositionsReport } from './Reports/DailyPositions/DailyPositionsReport';
import { MtmReport, MtmClosedReport, GrossHedgingReport, WeightedCostAvgReport, CombinedReport, OpenContractsReport, HedgingPositionReport, BudgetProvider } from './Reports/LegacyReports';
import { PiedmontHedgingReport } from './Reports/BatchReports';

interface Report extends CustomerReportModel {
  report: ReactElement;
}

export interface ReportProps {
  customerName?: string;
  customerReportId: number;
  customerData: GetCustomerDataResponse;
  customerSettings: GetCustomerSettingsResponse;
  quoteDate: Date;
}

export default function ReportsPage() {
  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
  const customerReports = useApplicationSelector((state: RootState): CustomerReportModel[] => state.commodityManagement.customerReports);
  const activeSite = useApplicationSelector((state: RootState): CommodityManagementSiteAccessModel => state.activeSite.site);

  const quoteDate = useMtmDate();
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [activeReports, setActiveReports] = useState<Report[]>([]);

  const buttonRef = useRef<HTMLDivElement>(null);
  const [ open, setOpen ] = useState(false);

  function handleTabChange(newIndex: number) {
    setActiveTabIndex(newIndex);
  }

  function handleTabClose(indexToClose: number) {
    const lastIndex = activeReports.length > 0 ? activeReports.length - 1 : 0;

    setActiveReports(activeReports.filter((report, i) => i !== indexToClose));

    if (activeReports.length > 1) {
      if (indexToClose == lastIndex) {
        setActiveTabIndex(indexToClose - 1);
      } 
      else if (indexToClose < activeTabIndex) {
        setActiveTabIndex(activeTabIndex - 1);
      }
      else if (indexToClose >= activeTabIndex) {
        // Do nothing
      }
    } else {
      setActiveTabIndex(0);
    }

  }

  function addReport(reportType: CustomerReportModel | null) {
    const lastIndex = activeReports.length - 1;

    if (reportType) {
      setActiveReports((previous) => [...previous, getNewReport(reportType)]);
    }

    setActiveTabIndex(lastIndex + 1); // Go to new report
    closeReportSelector();
  }

  function createNewReportElement(customerReport: CustomerReportModel): ReactElement {

    const reportProps = {
      customerName: activeSite.siteName ?? undefined,
      customerReportId: customerReport.customerReportId,
      customerData: customerData,
      customerSettings: customerSettings,
      quoteDate: quoteDate,
    }

    const legacyReportProps = {
      customerData: customerData,
      customerSettings: customerSettings,
      defaultQuoteDate: quoteDate,
    }

    switch (customerReport.reportType) {
      case ReportType.MarkToMarket: return <PositionReport {...reportProps} />;
      case ReportType.VarianceToBudget: return <VarianceReport {...reportProps} />;
      case ReportType.Scenario: return <ScenarioReport {...reportProps} />;
      case ReportType.TimeTrigger: return <TimeTriggerReport {...reportProps} />;
      case ReportType.ProfitAndLoss: return <ProfitLossReport {...reportProps} />;
      case ReportType.Summary: return <SummaryReport {...reportProps} />;
      case ReportType.Exposure: return <ExposureReport {...reportProps} />;
      case ReportType.DailyPositions: return <DailyPositionsReport {...reportProps} />;
      case ReportType.MarkToMarketClosed: return <MtmClosedReport {...legacyReportProps} />;
      case ReportType.GrossHedging: return <GrossHedgingReport {...legacyReportProps} />;
      case ReportType.WeightedCostAverage: return <WeightedCostAvgReport {...legacyReportProps} />;
      case ReportType.Combined: return <CombinedReport {...legacyReportProps} />;
      case ReportType.OpenContracts: return <OpenContractsReport {...legacyReportProps} customerName={activeSite.siteName ?? undefined} />;
      case ReportType.HedgingPosition: return <HedgingPositionReport {...legacyReportProps} />;
      case ReportType.HedgingMtm: return <MtmReport {...legacyReportProps} />;
      case ReportType.PiedmontHedging: return <PiedmontHedgingReport {...reportProps} />;
      default: return <>Error</>;
    }
  }

  function getNewReport(customerReport: CustomerReportModel): Report {
    return {
      customerReportId: customerReport.customerReportId,
      reportType: customerReport.reportType,
      reportName: customerReport.reportName,
      configuration: customerReport.configuration,
      userOverrides: customerReport.userOverrides,
      report: createNewReportElement(customerReport),
    };
  }

  function openReportSelector() {
    setOpen(true);
  };
  
  function closeReportSelector() {
    setOpen(false);
  };

  return (
    <BudgetProvider>
    <StoneXMainPage>
      <StoneXRow>
        <h1>Reports</h1>
      </StoneXRow>
{/* onTabChange={handleTabChange} onTabClick={(index: number) => index == activeReports.length && openReportSelector()} */}
      <StoneXTabs activeTabIndex={activeTabIndex}>
        {[
          ...activeReports.map((report, i) => (
            <StoneXTab key={i} label={report.reportName} tabIndex={i} isActive={activeTabIndex == i} showCloseButton onClick={() => handleTabChange(i)} onClose={(index: number) => handleTabClose(index)} />
          ))
          ,
          <StoneXTab key={-1} ref={buttonRef} tabIndex={-1} onClick={openReportSelector} label={<IconButton><Add fontSize="medium" /></IconButton>}></StoneXTab>
        ]}
      </StoneXTabs>

      <Popper open={open} anchorEl={buttonRef.current} placement="bottom-start" >
        <ClickAwayListener onClickAway={closeReportSelector}>
          <Paper>
            <MenuList autoFocusItem={open}>
              {customerReports
                .filter(x => x.reportName?.toLowerCase() !== 'position')
                .map(x => (
                  <MenuItem key={x.customerReportId} onClick={() => addReport(x)}>
                    {x.reportName}
                  </MenuItem>
                ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>

      <>
        {activeReports.map((report, i) => {return <div key={i} style={{  display: i == activeTabIndex ? 'flex' : 'none', flexDirection: 'column', flexGrow: 1, margin: '20px'}}>{report.report}</div>;})}
      </>

      {activeReports.length == 0 && <div style={{display: 'flex', flexGrow: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
        <StoneXRow>
          <Insights sx={{fontSize: '4em', color: 'var(--dark-secondary-blue)'}}/>
        </StoneXRow>
        <StoneXRow>
          <h1>Reports</h1>
        </StoneXRow>
        <StoneXRow>
          There are no reports to show
        </StoneXRow>
        <StoneXRow>
          <Button onClick={openReportSelector}>Add a report</Button>
        </StoneXRow>
      </div>}

    </StoneXMainPage>
    </BudgetProvider>
  );
}
